// Phase 17 Plan 02 Task 1 — Piece primitive.
//
// Chess-piece geometry primitive consuming the `.piece` recipe in
// apps/web/src/styles/pieces.css. The CSS recipe handles base
// silhouette per `kind`, side tint per `player` (a / b), and state
// mods (empowered, selected, breathe). This component is a
// strict-typed React shell that composes className + ARIA only.
//
// Recipe source: apps/web/src/styles/pieces.css `.piece` (take-two re-skin).
//
// ARIA contract (UI-SPEC §5.1 row 2): the piece always renders a
// role="img" with an English aria-label like "knight, player A".
// i18n routing of the label is deferred to Phase 18 — the current
// label string is hard-coded English per the plan's
// `ariaLabelFor()` decision.
//
// The `.base` + `.body` sub-divs are decorative geometry layers (the
// CSS recipe drives clip-paths + filters); they carry aria-hidden
// so screen readers see the parent role/label only.

import type { CSSProperties, JSX } from 'react'

export type PieceKind =
  | 'king'
  | 'queen'
  | 'knight'
  | 'bishop'
  | 'rook'
  | 'blank'
export type PlayerSide = 'a' | 'b'

export interface PieceProps {
  kind: PieceKind
  player: PlayerSide
  empowered?: boolean
  selected?: boolean
  size?: number
}

/** English aria-label used until Phase 18 i18n routes it. */
function ariaLabelFor(kind: PieceKind, player: PlayerSide): string {
  return `${kind}, player ${player.toUpperCase()}`
}

export function Piece({
  kind,
  player,
  empowered = false,
  selected = false,
  size,
}: PieceProps): JSX.Element {
  const classes = [
    'piece',
    player,
    kind,
    empowered ? 'empowered' : null,
    selected ? 'selected' : null,
  ]
    .filter(Boolean)
    .join(' ')

  // Pass piece-size through as a CSS var so the .piece recipe's
  // `width: var(--piece-size, 48px)` picks it up. This is the
  // prototype's contract (rg-components.jsx Piece). Note: this is
  // NOT a local override of a token (Pitfall 3) — `--piece-size`
  // is a component-scoped sizing variable, not a `--color-*` /
  // `--space-*` / `--radius-*` / `--font-size-*` token.
  const style: CSSProperties | undefined =
    size !== undefined
      ? ({ '--piece-size': `${size}px` } as CSSProperties)
      : undefined

  return (
    <div
      className={classes}
      role="img"
      aria-label={ariaLabelFor(kind, player)}
      style={style}
    >
      <div className="base" aria-hidden="true" />
      <div className="body" aria-hidden="true" />
    </div>
  )
}
